import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, Loader2, Mail, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/cd5s20vv_isalon2022-01.png";

const LoginPage = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) {
      navigate('/backoffice', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success('Welcome back');
      navigate('/backoffice', { replace: true });
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6" data-testid="login-page">
      <Card className="max-w-sm w-full bg-zinc-900 border-white/10">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-isalonuk-accent/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock size={30} className="text-isalonuk-accent" />
            </div>
            <img src={LOGO_URL} alt="iSalon UK" className="h-8 mx-auto mb-3" />
            <h1 className="font-heading text-2xl text-white">Back Office</h1>
            <p className="text-white/60 font-sans text-sm mt-1">Sign in to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                autoComplete="username"
                className="bg-black border-white/20 text-white pl-10"
                data-testid="login-email-input"
              />
            </div>
            <div className="relative">
              <KeyRound size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                autoComplete="current-password"
                className="bg-black border-white/20 text-white pl-10"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-semibold rounded-full"
              data-testid="login-submit"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : 'Sign In'}
            </Button>
          </form>

          <div className="text-center mt-6">
            <Link to="/" className="text-isalonuk-accent font-sans text-sm hover:underline">
              ← Back to Home
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
